import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUnleashContext } from '@unleash/proxy-client-react'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useCookies } from 'react-cookie'
import { useLocation, useNavigate } from 'react-router'

import { IMPERSONATION_BANNER_CLOSED_KEY } from '@/components/ImpersonatingBanner/constants'
import { errorMessage } from '@/components/Message'
import { debugTrackingEvents } from '@/heap/debug/debugEvents'
import { usePreviousValue } from '@/hooks/usePreviousValue'
import { useStringUrlQueryState } from '@/hooks/useUrlQueryState'
import { CognitoMonitoringEvents } from '@/monitoring/newRelic/CognitoMonitoringEvents'
import { PATHS } from '@/routes/PATHS'
import { authService, type UserAttributes } from '@/services/api/auth.service'
import { usePremiumFeaturesAccountCreatedAt } from '@/stores/permissions/premiumFeatures'
import {
  IMPERSONATION_COOKIE_NAME,
  IMPERSONATION_COOKIE_SETTINGS,
  MARQII_COGNITO_CLIENT_ID,
  MARQII_COGNITO_COOKIE_SETTINGS,
} from '@/utils/constants/oauth'

export interface CognitoAuthContextType {
  userId: string
  userAttributes?: UserAttributes
  accountId: string
  accessToken: string
  isAuthenticatedPreviousValue: boolean
  isAuthenticated: boolean
  isFirstTimeLogin: boolean
  loading: boolean
  impersonation: {
    isImpersonating: boolean
  }
  login: (input: { email: string; password: string }) => void
  logout: () => void
  updateUserAttributes: (data: Partial<UserAttributes>) => Promise<void>
  changePassword: (input: { oldPassword: string; newPassword: string }) => Promise<void>
  forgotPassword: (input: { email: string }) => Promise<void>
  resetPassword: (input: { email: string; code: string; password: string }) => Promise<void>
  changeTemporaryPassword: (input: {
    newPassword: string
    optInMarketing?: boolean
  }) => Promise<void>
  getAccessToken: () => Promise<string | undefined>
}

const CognitoAuthContext = createContext<CognitoAuthContextType>({} as CognitoAuthContextType)

const isProduction = process.env.NODE_ENV === 'production'

function useAuthSession() {
  return useQuery({
    queryKey: ['auth-session'],
    queryFn: () => authService.fetchAuthSession(),
    enabled: true,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

function useAuthUserAttributes() {
  return useQuery({
    queryKey: ['auth-user-attributes'],
    queryFn: () => authService.fetchUserAttributes() as unknown as UserAttributes,
    enabled: true,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

function useAuthLogin() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.signIn({ username: email, password }),
    onSuccess: async (data) => {
      if (data.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        await navigate(PATHS.newPasswordPage, { replace: true })
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['auth-session'] }),
          queryClient.invalidateQueries({ queryKey: ['auth-user-attributes'] }),
        ])
      }
    },
    onError: (error) => {
      console.error('Error on sign in:', error)
      errorMessage(error.message)
    },
  })
}

function useAuthLogout() {
  const queryClient = useQueryClient()
  const [_, __, removeCookie] = useCookies([IMPERSONATION_COOKIE_NAME, MARQII_COGNITO_CLIENT_ID])
  return useMutation({
    mutationFn: async () => {
      removeCookie(IMPERSONATION_COOKIE_NAME, IMPERSONATION_COOKIE_SETTINGS)
      removeCookie(MARQII_COGNITO_CLIENT_ID, MARQII_COGNITO_COOKIE_SETTINGS)
      await authService.signOut()
      localStorage.removeItem(IMPERSONATION_BANNER_CLOSED_KEY)
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth-user-attributes'], () => null)
      queryClient.setQueryData(['auth-session'], () => null)
    },
    onError: (error) => {
      console.error('Error on sign out:', error)
      errorMessage(error.message)
    },
    onSettled: () => {
      window.heap.clearEventProperties()
      window.heap.addEventProperties({ loggedIn: 'false' })
    },
  })
}

function useAuthForgotPassword() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      await authService.resetPassword({ username: email })
    },
    onError: (error) => {
      console.error('Error on forgot:', error)
      errorMessage(error.message)
    },
  })
}

function useAuthResetPassword() {
  return useMutation({
    mutationFn: async ({
      email,
      code,
      password,
    }: {
      email: string
      code: string
      password: string
    }) => {
      await authService.confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      })
    },
    onError: (error) => {
      console.error('Error on reset:', error)
      errorMessage(error.message)
    },
  })
}

function useAuthChangeTemporaryPassword() {
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async ({
      newPassword,
      optInMarketing,
    }: {
      newPassword: string
      optInMarketing?: boolean
    }) => {
      await authService.confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: optInMarketing ? { 'custom:optInMarketing': '1' } : undefined },
      })
    },
    onSuccess: () => navigate(PATHS.signInPage, { replace: true }),
    onError: (error) => {
      console.error('Error on change temporary password:', error)
      errorMessage(error.message)
    },
  })
}

function useAuthChangePassword() {
  return useMutation({
    mutationFn: async ({
      oldPassword,
      newPassword,
    }: {
      oldPassword: string
      newPassword: string
    }) => {
      await authService.updatePassword({ oldPassword, newPassword })
    },
    onError: (error) => {
      console.error('Error on change password:', error)
    },
  })
}

function useAuthUpdateUserAttributes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userAttributes: Partial<UserAttributes>) => {
      await authService.updateUserAttributes({ userAttributes })
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth-session'] }),
        queryClient.invalidateQueries({ queryKey: ['auth-user-attributes'] }),
      ]),
    onError: (error) => {
      console.error('Error on fetch currentSession', error)
    },
  })
}

export function CognitoAuthProvider({ children }: React.PropsWithChildren): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const updateContext = useUnleashContext()
  const accountCreatedAt = usePremiumFeaturesAccountCreatedAt()
  const [redirectUri] = useStringUrlQueryState('redirect_uri')
  const session = useAuthSession()
  const userAttributes = useAuthUserAttributes()
  const login = useAuthLogin()
  const logout = useAuthLogout()
  const forgotPassword = useAuthForgotPassword()
  const resetPassword = useAuthResetPassword()
  const changeTemporaryPassword = useAuthChangeTemporaryPassword()
  const changePassword = useAuthChangePassword()
  const updateUserAttributes = useAuthUpdateUserAttributes()

  const loading =
    session.isLoading ||
    userAttributes.isLoading ||
    login.isPending ||
    forgotPassword.isPending ||
    resetPassword.isPending
  const loadingInitial = session.isPending || userAttributes.isPending

  const accessToken = session.data?.tokens?.accessToken.toString() || ''
  const isAuthenticated = !!accessToken
  const isAuthenticatedPreviousValue = usePreviousValue(isAuthenticated)

  const isFirstTimeLogin =
    login.data?.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'

  const payload = session.data?.tokens?.accessToken.payload
  const cognitoGroups = payload?.['cognito:groups']
  const hasGroups = Array.isArray(cognitoGroups) && cognitoGroups.length > 0
  const isImpersonating = hasGroups && cognitoGroups.length > 1
  const accountId = hasGroups ? (cognitoGroups[0] as string) : ''
  const userId = userAttributes.data?.sub || ''

  const originAccountId = !isFirstTimeLogin ? userAttributes.data?.['custom:tenantId'] : ''

  useEffect(() => {
    if (!isAuthenticated) return

    const authPathsToList = Object.values(PATHS)
      .filter((path) => path.includes('/auth'))
      .map((p) => String(p))

    if (redirectUri) {
      void navigate(decodeURIComponent(redirectUri), { replace: true })
    } else if (authPathsToList.includes(location.pathname)) {
      void navigate(PATHS.homePage, { replace: true })
    }
    // only redirect when the isAuthenticated changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    const properties = {
      accountId,
      ...(accountCreatedAt ? { accountCreatedAt: accountCreatedAt.getTime().toString() } : {}),
    }

    void updateContext({ properties, userId })

    if (userId && accountId) {
      window.heap.identify(userId)
      window.heap.addUserProperties({ accountId: originAccountId || '' })
      window.heap.clearEventProperties()
      window.heap.addEventProperties({ accountId, loggedIn: 'true' })
    }

    if (!window.newrelic) {
      if (isProduction) console.warn('New Relic not initialized.')
      return
    }

    if (userId && !accountId) {
      debugTrackingEvents.loggedUserWithoutAccountId(userId)
      window?.newrelic?.addPageAction(CognitoMonitoringEvents.loggedUserWithoutAccountId, {
        accountId,
        originAccountId,
        userId,
      })
    }
  }, [accountId, originAccountId, userId, accountCreatedAt, updateContext])

  const getAccessToken = useCallback(async () => {
    const { data } = await session.refetch()
    return data?.tokens?.accessToken.toString()
  }, [session])

  // Make the provider update only when it should.
  // We only want to force re-renders if the user,
  // loading or error states change.
  const memoedValue: CognitoAuthContextType = useMemo(
    () => ({
      accessToken,
      accountId,
      isAuthenticated,
      isAuthenticatedPreviousValue,
      isFirstTimeLogin,
      loading,
      userId,
      userAttributes: userAttributes.data,
      login: login.mutate,
      logout: logout.mutate,
      changePassword: changePassword.mutateAsync,
      updateUserAttributes: updateUserAttributes.mutateAsync,
      forgotPassword: forgotPassword.mutateAsync,
      resetPassword: resetPassword.mutateAsync,
      changeTemporaryPassword: changeTemporaryPassword.mutateAsync,
      impersonation: {
        isImpersonating,
      },
      getAccessToken,
    }),
    // ignoring exhaustive deps to only re-render children if 'important' state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accessToken,
      accountId,
      isAuthenticated,
      isFirstTimeLogin,
      loading,
      userId,
      userAttributes.data,
    ],
  )

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <CognitoAuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </CognitoAuthContext.Provider>
  )
}

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export function useCognitoAuth() {
  return useContext(CognitoAuthContext)
}
